import type { IModalProps, IPivotItemProps } from "@fluentui/react";
import { Modal, PrimaryButton, Pivot, PivotItem } from "@fluentui/react";
import QRCode from "qrcode.react";
import type { CurrentArtifactVersions, DownloadToken } from "../../api";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { store } from "../../store";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Wifi, ChevronDown } from "lucide-react";

const QRCodePlaceholder = (): JSX.Element => (
    <div className="h-60 w-60 border-2 border-dashed border-gray-400 bg-gray-100" />
);

const SectionSubtitle = ({ children }: React.PropsWithChildren): JSX.Element => (
    <h2 className="my-2 text-lg font-semibold">{children}</h2>
);

const Section = ({ children }: React.PropsWithChildren): JSX.Element => (
    <div className="my-4 flex flex-col items-center justify-evenly md:flex-row">{children}</div>
);

const MissingArtifactAlert = (): JSX.Element => {
    const { t } = useTranslation();

    return (
        <Alert variant="warning" className="my-2">
            <AlertTitle>{t("component.modalAndroidInfo.missingArtifact.title")}</AlertTitle>
            <AlertDescription>
                {t("component.modalAndroidInfo.missingArtifact.description")}
            </AlertDescription>
        </Alert>
    );
};

const SectionSeparator = (): JSX.Element => {
    const { t } = useTranslation();

    return (
        <>
            {/* Mobile version / horizontal separator */}
            <div className="flex h-12 w-full max-w-60 items-center justify-center gap-2 md:hidden">
                <Separator orientation="horizontal" className="grow" />
                <span className="text-muted-foreground text-xs leading-normal uppercase">
                    {t("component.modalAndroidInfo.or")}
                </span>
                <Separator orientation="horizontal" className="grow" />
            </div>
            {/* Desktop version / vertical separator */}
            <div className="hidden h-30 flex-col items-center justify-center gap-2 md:flex">
                <Separator orientation="vertical" className="grow" />
                <span className="text-muted-foreground text-xs leading-normal uppercase">
                    {t("component.modalAndroidInfo.or")}
                </span>
                <Separator orientation="vertical" className="grow" />
            </div>
        </>
    );
};

// Renders the "First Step" download section (button + QR code) for a single app.
const DownloadStep = ({
    appName,
    downloadLink,
}: {
    appName: string;
    downloadLink?: string;
}): JSX.Element => {
    const { t } = useTranslation();

    return (
        <>
            {!downloadLink && <MissingArtifactAlert />}
            <SectionSubtitle>{t("component.modalAndroidInfo.step1.title")}</SectionSubtitle>
            <p>{t("component.modalAndroidInfo.step1.description", { app: appName })}</p>
            <Section>
                <PrimaryButton
                    text={t("component.modalAndroidInfo.download.text", { app: appName })}
                    href={downloadLink}
                    download
                    disabled={!downloadLink}
                />
                <SectionSeparator />
                {downloadLink ? <QRCode size={240} value={downloadLink} /> : <QRCodePlaceholder />}
            </Section>
        </>
    );
};

// A single numbered step of the device setup instructions.
const SetupStep = ({
    number,
    title,
    children,
}: React.PropsWithChildren<{ number: number; title: string }>): JSX.Element => (
    <li className="flex gap-4">
        <span className="bg-primary text-primary-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-semibold">
            {number}
        </span>
        <div>
            <h3 className="font-semibold">{title}</h3>
            <p className="text-muted-foreground mt-1 text-sm">{children}</p>
        </div>
    </li>
);

const TAB_ELOOPERATOR = "elooperator";
const TAB_DEVICE_SETUP = "deviceSetup";
const TAB_ELOFLEET = "elofleet";
const TAB_ELOSERVICE = "eloservice";

// Brand names used for interpolating app-specific instruction texts.
const APP_NAMES: Record<string, string> = {
    [TAB_ELOOPERATOR]: "ELOoperator",
    [TAB_ELOFLEET]: "ELOfleet",
    [TAB_ELOSERVICE]: "ELOservice",
};

export function ModalAndroidInfo(props: IModalProps): JSX.Element {
    const i18n = store.i18n;
    const { t } = useTranslation();
    const artifactsApi = store.artifactsApi;

    const [selectedKey, setSelectedKey] = useState<string>(TAB_DEVICE_SETUP);

    // Authenticates an anonymous user for downloading the android application
    const [downloadToken, setDownloadToken] = useState<DownloadToken | undefined>(undefined);
    const [versions, setVersions] = useState<CurrentArtifactVersions | undefined>(undefined);
    const [downloadLinkElooperator, setDownloadLinkElooperator] = useState<string | undefined>(
        undefined,
    );
    const [downloadLinkElofleet, setDownloadLinkElofleet] = useState<string | undefined>(undefined);
    const [downloadLinkEloservice, setDownloadLinkForEloservice] = useState<string | undefined>(
        undefined,
    );
    const [provisioningQrPayload, setProvisioningQrPayload] = useState<string | undefined>(
        undefined,
    );
    // Optional WiFi to pre-fill into the device setup QR code (entered by the operator, never
    // sent to the backend). When an SSID is given, the device auto-joins during provisioning.
    const [wifiOpen, setWifiOpen] = useState(false);
    const [wifiSsid, setWifiSsid] = useState("");
    const [wifiPassword, setWifiPassword] = useState("");

    // Fetch a download token to store inside the QR code. This way
    // users can download the mobile application onto their smartphones
    // without the need of a login token.
    useEffect(() => {
        artifactsApi.artifactsGetDownloadToken().then(setDownloadToken);
        artifactsApi.artifactsGetCurrentVersions().then(setVersions);
        artifactsApi
            .artifactsGetElooperatorProvisioningPayload()
            .then((response) => setProvisioningQrPayload(response.payload));
    }, [artifactsApi]);

    useMemo(() => {
        // Don't do anything until we have all necessary components.
        if (versions == undefined || downloadToken == undefined) {
            return;
        }

        // Assemble the android app download link with a custom login token.
        const base = window.location.origin;
        const versionString = i18n.formatVersion(versions.version);
        const token = encodeURIComponent(downloadToken.token);
        const loginTokenId = encodeURIComponent(downloadToken.loginTokenId);
        const downloadUrlElooperator = `${base}/api/v1/artifacts/android_elooperator/${versionString}?token=${token}&login_token_id=${loginTokenId}`;
        const downloadUrlElofleet = `${base}/api/v1/artifacts/android_app/${versionString}?token=${token}&login_token_id=${loginTokenId}`;
        const downloadUrlEloservice = `${base}/api/v1/artifacts/android_eloservice/${versionString}?token=${token}&login_token_id=${loginTokenId}`;

        setDownloadLinkElooperator(downloadUrlElooperator);
        setDownloadLinkElofleet(downloadUrlElofleet);
        setDownloadLinkForEloservice(downloadUrlEloservice);
    }, [i18n, versions, downloadToken]);

    // The backend assembles the provisioning payload; if the operator entered a WiFi SSID we
    // inject the Android Enterprise WiFi keys into it client-side (the password never touches
    // the backend). An empty SSID leaves the payload unchanged.
    const deviceSetupQrPayload = useMemo(() => {
        if (provisioningQrPayload == undefined) {
            return undefined;
        }
        const ssid = wifiSsid.trim();
        if (ssid === "") {
            return provisioningQrPayload;
        }
        try {
            const payload = JSON.parse(provisioningQrPayload) as Record<string, unknown>;
            payload["android.app.extra.PROVISIONING_WIFI_SSID"] = ssid;
            if (wifiPassword !== "") {
                payload["android.app.extra.PROVISIONING_WIFI_PASSWORD"] = wifiPassword;
                payload["android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE"] = "WPA";
            } else {
                payload["android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE"] = "NONE";
            }
            return JSON.stringify(payload);
        } catch {
            return provisioningQrPayload;
        }
    }, [provisioningQrPayload, wifiSsid, wifiPassword]);

    return (
        <Modal {...props} styles={{ main: [{ overflow: "hidden" }] }}>
            <div className="preflight w-full p-8 md:min-h-[60rem] md:w-[42rem] md:p-12">
                <h1 className="text-center text-2xl font-semibold">
                    {t("component.modalAndroidInfo.title")}
                </h1>
                <Pivot
                    selectedKey={selectedKey}
                    onLinkClick={(item) => {
                        if (item?.props.itemKey) {
                            setSelectedKey(item.props.itemKey);
                        }
                    }}
                >
                    <PivotItem
                        itemKey={TAB_DEVICE_SETUP}
                        headerText={t("component.modalAndroidInfo.deviceSetup.tab")}
                    >
                        {!provisioningQrPayload && <MissingArtifactAlert />}
                        <p className="my-4">{t("component.modalAndroidInfo.deviceSetup.intro")}</p>
                        <Alert variant="warning" className="my-4">
                            <AlertTitle>
                                {t("component.modalAndroidInfo.deviceSetup.warning.title")}
                            </AlertTitle>
                            <AlertDescription>
                                {t("component.modalAndroidInfo.deviceSetup.warning.description")}
                            </AlertDescription>
                        </Alert>
                        <ol className="my-6 flex flex-col gap-5">
                            <SetupStep
                                number={1}
                                title={t("component.modalAndroidInfo.deviceSetup.step1.title")}
                            >
                                {t("component.modalAndroidInfo.deviceSetup.step1.description")}
                            </SetupStep>
                            <SetupStep
                                number={2}
                                title={t("component.modalAndroidInfo.deviceSetup.step2.title")}
                            >
                                {t("component.modalAndroidInfo.deviceSetup.step2.description")}
                            </SetupStep>
                            <SetupStep
                                number={3}
                                title={t("component.modalAndroidInfo.deviceSetup.step3.title")}
                            >
                                {t("component.modalAndroidInfo.deviceSetup.step3.description")}
                            </SetupStep>
                        </ol>
                        <div className="my-6 flex justify-center">
                            {provisioningQrPayload ? (
                                <QRCode
                                    size={280}
                                    value={deviceSetupQrPayload ?? provisioningQrPayload}
                                />
                            ) : (
                                <QRCodePlaceholder />
                            )}
                        </div>
                        <Collapsible open={wifiOpen} onOpenChange={setWifiOpen} className="my-6">
                            <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between rounded-md border px-4 py-3 text-sm font-medium transition-colors">
                                <span className="inline-flex items-center gap-2">
                                    <Wifi size={16} />
                                    {t("component.modalAndroidInfo.deviceSetup.wifi.toggle")}
                                </span>
                                <ChevronDown
                                    size={16}
                                    className={`transition-transform ${wifiOpen ? "rotate-180" : ""}`}
                                />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-3 flex flex-col gap-3">
                                <p className="text-muted-foreground text-sm">
                                    {t("component.modalAndroidInfo.deviceSetup.wifi.hint")}
                                </p>
                                <div className="flex flex-col gap-1">
                                    <Label htmlFor="wifi-ssid">
                                        {t("component.modalAndroidInfo.deviceSetup.wifi.ssidLabel")}
                                    </Label>
                                    <Input
                                        id="wifi-ssid"
                                        startIcon={Wifi}
                                        autoComplete="off"
                                        value={wifiSsid}
                                        onChange={(e) => setWifiSsid(e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <Label htmlFor="wifi-password">
                                        {t(
                                            "component.modalAndroidInfo.deviceSetup.wifi.passwordLabel",
                                        )}
                                    </Label>
                                    <PasswordInput
                                        id="wifi-password"
                                        autoComplete="off"
                                        value={wifiPassword}
                                        onChange={(e) => setWifiPassword(e.target.value)}
                                    />
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </PivotItem>
                    <PivotItem
                        itemKey={TAB_ELOOPERATOR}
                        headerText="ELOoperator"
                        onRenderItemLink={(
                            linkProps?: IPivotItemProps,
                            defaultRender?: (props?: IPivotItemProps) => JSX.Element | null,
                        ) => (
                            <span className="inline-flex items-center gap-2">
                                {defaultRender?.(linkProps)}
                                <Badge variant="lightGreen" className="uppercase">
                                    {t("component.modalAndroidInfo.recommended")}
                                </Badge>
                            </span>
                        )}
                    >
                        <DownloadStep
                            appName={APP_NAMES[TAB_ELOOPERATOR]}
                            downloadLink={downloadLinkElooperator}
                        />
                    </PivotItem>
                    <PivotItem
                        itemKey={TAB_ELOFLEET}
                        headerText="ELOfleet"
                        onRenderItemLink={(
                            linkProps?: IPivotItemProps,
                            defaultRender?: (props?: IPivotItemProps) => JSX.Element | null,
                        ) => (
                            <span className="inline-flex items-center gap-2">
                                {defaultRender?.(linkProps)}
                                <Badge variant="secondary" className="uppercase">
                                    {t("component.modalAndroidInfo.legacy")}
                                </Badge>
                            </span>
                        )}
                    >
                        <DownloadStep
                            appName={APP_NAMES[TAB_ELOFLEET]}
                            downloadLink={downloadLinkElofleet}
                        />
                    </PivotItem>
                    <PivotItem itemKey={TAB_ELOSERVICE} headerText="ELOservice">
                        <DownloadStep
                            appName={APP_NAMES[TAB_ELOSERVICE]}
                            downloadLink={downloadLinkEloservice}
                        />
                    </PivotItem>
                </Pivot>
                {/* The server URL step doesn't apply to the device setup flow — there, the
                    provisioning QR code configures the server URL automatically. */}
                {selectedKey !== TAB_DEVICE_SETUP && (
                    <div>
                        <SectionSubtitle>
                            {t("component.modalAndroidInfo.step2.title")}
                        </SectionSubtitle>
                        <p>
                            {t("component.modalAndroidInfo.step2.description", {
                                app: APP_NAMES[selectedKey],
                            })}
                        </p>
                        <Section>
                            <div className="w-48 text-center font-bold">
                                {window.location.origin}
                            </div>
                            <SectionSeparator />
                            <QRCode size={240} value={window.location.origin} />
                        </Section>
                    </div>
                )}
                {versions && (
                    <div className="mt-8 text-center text-sm">
                        <strong>{t("component.modalAndroidInfo.version")}</strong>
                        {": "}
                        {i18n.formatVersion(versions.version)}
                    </div>
                )}
            </div>
        </Modal>
    );
}
